/**
 * wildCards.ts — WILD CARDS v1 (BETA 22.89, Dylan's system).
 *
 * The 23 Wild Cards are the surviving buncha-bullshiiii effects wearing new
 * identities (Dylan's wild_cards_cleaned_pack: "keep the old effects intact").
 * Effects stay implemented under their ORIGINAL item ids in
 * bunchaBullshiiii/itemEffectSystem — this module is the identity overlay:
 * card name, baked card art, and the 1-word category on the bottom plate.
 *
 * Design separation (Dylan's law):
 *   Level-up = Perk Cards + Weapon Upgrades.
 *   WILD CARD MACHINE (tokens) = Wild Cards.   ← this module
 *   Shrines = shrine buffs.
 *
 * Rarity model mirrors perk cards: the machine rolls the TIER of the pull;
 * higher tiers grant more STACKS of the same base effect (×1/×2/×3/×5 —
 * Dylan's perk-card multiplier pattern; base = the old approved values).
 */

export interface WildCardDef {
  itemId: string;    // ORIGINAL buncha item id — effect wiring keys on this
  name: string;      // card display name (top plate)
  cardFile: string;  // /wild-cards/<tier>/<cardFile>.webp
  category: string;  // ONE word, bottom plate
}

export const WILD_CARDS: WildCardDef[] = [
  { itemId: 'C03',  name: 'BULLY',              cardFile: '01_bully',              category: 'DAMAGE' },
  { itemId: 'C02',  name: 'THICK SKIN',         cardFile: '02_thick_skin',         category: 'SURVIVAL' },
  { itemId: 'C06',  name: 'BIG BONED',          cardFile: '03_big_boned',          category: 'SURVIVAL' },
  { itemId: 'C05',  name: 'XP FARM',            cardFile: '04_xp_farm',            category: 'ECONOMY' },
  { itemId: 'C07',  name: 'SUCK MORE',          cardFile: '05_suck_more',          category: 'UTILITY' },
  { itemId: 'R02',  name: 'LUCKY DUCK',         cardFile: '06_lucky_duck',         category: 'UTILITY' },
  { itemId: 'C08',  name: 'TRIGGER HAPPY',      cardFile: '07_trigger_happy',      category: 'DAMAGE' },
  { itemId: 'C09',  name: 'BOOM MORPHED AGAIN', cardFile: '08_boom_morphed_again', category: 'SURVIVAL' },
  { itemId: 'C10',  name: 'LAST LONGER',        cardFile: '09_last_longer',        category: 'SURVIVAL' },
  { itemId: 'UC03', name: 'PANIC PUMP',         cardFile: '10_panic_pump',         category: 'DAMAGE' },
  { itemId: 'R01',  name: 'NO PAIN NO GAIN',    cardFile: '11_no_pain_no_gain',    category: 'DAMAGE' },
  { itemId: 'R03',  name: 'PAIN MAIL',          cardFile: '12_pain_mail',          category: 'SURVIVAL' },
  { itemId: 'R05',  name: 'LOVE BITE',          cardFile: '13_love_bite',          category: 'SURVIVAL' },
  { itemId: 'R07',  name: 'FAST FEET',          cardFile: '15_fast_feet',          category: 'MOVEMENT' },
  { itemId: 'R08',  name: 'POCKET ROCKET',      cardFile: '16_pocket_rocket',      category: 'PROJECTILE' },
  { itemId: 'R09',  name: 'XPLOSIVE',           cardFile: '17_xplosive',           category: 'UTILITY' },
  { itemId: 'R10',  name: 'OFF LIMITS',         cardFile: '18_off_limits',         category: 'SURVIVAL' },
  { itemId: 'R11',  name: 'CROP DUSTER',        cardFile: '19_crop_duster',        category: 'POISON' },
  { itemId: 'R12',  name: 'TOXIC LOVER',        cardFile: '20_toxic_lover',        category: 'POISON' },
  { itemId: 'R13',  name: 'DESPERATE MEASURES', cardFile: '21_desperate_measures', category: 'DAMAGE' },
  { itemId: 'R15',  name: 'BODY COUNT',         cardFile: '22_body_count',         category: 'DAMAGE' },
  { itemId: 'R16',  name: 'SIDE PIECE',         cardFile: '23_side_piece',         category: 'SUMMON' },
  { itemId: 'R17',  name: 'GORE GLITTER',       cardFile: '24_gore_glitter',       category: 'DAMAGE' },
];

export const WILD_CARD_BY_ITEM: Record<string, WildCardDef> =
  Object.fromEntries(WILD_CARDS.map(w => [w.itemId, w]));

const _TIER_DIR: Record<string, string> = {
  common: 'common', uncommon: 'common', rare: 'rare', epic: 'epic', legendary: 'legendary',
};

/** Baked card art (shell + art + WILD CARD badge, transparent pad margin). */
export function wildCardArtPath(itemId: string, tier: string): string | null {
  const wc = WILD_CARD_BY_ITEM[itemId];
  if (!wc) return null;
  return `/wild-cards/${_TIER_DIR[tier] ?? 'common'}/${wc.cardFile}.webp`;
}

/** BETA 22.90 — square art-only icon (128px, from the raw card art) for the
 *  HUD slot grid / pause LOOT / tooltips. Dylan: "still using the old buncha
 *  bullshit items in the ui in the bottom right. i need all of that deleted"
 *  — the 23 items' iconUrl points here now; old item PNGs render nowhere. */
export function wildCardIconPath(itemId: string): string | null {
  const wc = WILD_CARD_BY_ITEM[itemId];
  if (!wc) return null;
  return `/wild-cards/icons/${wc.cardFile}.webp`;
}

/** The baked canvases carry a transparent margin so the badge overhangs the
 *  card. This is the CARD BODY region inside the image (fractions x0,y0,x1,y1)
 *  — draw the image scaled so THIS region fills the card rect. */
export const WILD_CARD_IMG_BODY: [number, number, number, number] =
  [190 / 892, 190 / 1148, 702 / 892, 958 / 1148];

/** Tier of the pull → stacks of the base effect (Dylan's ×1/2/3/5 pattern —
 *  common tier == the exact old approved item values). */
export const WILD_CARD_TIER_STACKS: Record<string, number> = {
  common: 1, uncommon: 1, rare: 2, epic: 3, legendary: 5,
};
